/**
 * Which deployment this bundle is talking to, for Sentry's `environment` tag.
 *
 * @author Milton Filho
 */

/**
 * Derived from the API base URL rather than from a new build argument. The
 *   caller passes `baseURL` so there is one source for it, not two.
 *
 * That variable is already environment-scoped in GitLab and already baked into
 *   the bundle at build time — verified against both live deploys: the staging
 *   bundle carries `api.stage.petehealth.com` and the production one carries
 *   `api.petehealth.com`. Adding a `VITE_ENVIRONMENT` arg would mean touching
 *   the Dockerfile, `.gitlab-ci.yml` and the GitLab variable settings to learn
 *   something the bundle already knows.
 *
 * `import.meta.env.MODE` is not usable here: `vite build` reports `production`
 *   for the staging image too, so it cannot tell the two deployments apart —
 *   which is the entire point of the tag.
 *
 * **The unrecognised case resolves to `production`, deliberately.** The failure
 *   that costs something is a real production error filtered out of view
 *   because it was labelled `development`. Under-reporting noise is cheaper
 *   than hiding an incident, so anything on a `petehealth.com` host that is not
 *   recognisably staging counts as production. Only a host that is neither —
 *   `localhost`, a preview, a test — falls through to `development`.
 */
export const getSentryEnvironment = (apiUrl: string | undefined): string => {
  if (!apiUrl) return 'unknown';

  let hostname: string;
  try {
    hostname = new URL(apiUrl).hostname.toLowerCase();
  } catch {
    return 'unknown';
  }

  // Checked before the production branch: `api.stage.petehealth.com` satisfies
  //   both, and the more specific one has to win.
  if (hostname.includes('stage')) return 'staging';
  if (hostname === 'petehealth.com' || hostname.endsWith('.petehealth.com')) {
    return 'production';
  }
  return 'development';
};
